/**
 * TakenKlantDrawer — klant- + order/offerte-context als slide-over op /telefoon
 * (Gesprekken-tab). Spiegelt de inbox-rechterrail: haalt de CustomerMatch op via
 * /api/customers/match en hergebruikt <KlantinfoRail> + <InboxDrawerPanel> zodat
 * je vanuit een gesprek direct de Klant/WO opent zonder de tab te verlaten.
 *
 * De Opvolgen-tab gebruikt hiervoor de in-flow split-variant <TakenKlantPanel>.
 *
 * Identificatie: customerId + customerType, het type zoals de server het uit de
 * mirror haalde (gesprek: conversations.customer_type, taak: klant.type). Nooit
 * uit de id-prefix: eigen ids van na de cut hebben er geen (#164). Zonder
 * type valt hij terug op het belnummer (phone-lookup vindt account of lead).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { CustomerMatch } from '../../stores/inbox-store';
import { useInboxDrawerStore } from '../../stores/inbox-drawer-store';
import { KlantinfoRail } from '../inbox/KlantinfoRail';
import { InboxDrawerPanel } from '../inbox/InboxDrawerPanel';

interface TakenKlantDrawerProps {
  customerId: string | null;
  customerType: 'account' | 'lead' | null;
  phone: string | null;
  fallbackName: string | null;
  onClose: () => void;
}

export function TakenKlantDrawer({ customerId, customerType, phone, fallbackName, onClose }: TakenKlantDrawerProps) {
  const [match, setMatch] = useState<CustomerMatch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const openDrawer = useInboxDrawerStore((s) => s.open);
  const closeDrawer = useInboxDrawerStore((s) => s.close);
  const drawerOpenItem = useInboxDrawerStore((s) => s.openItem);

  // Haal de klant + orders/leads op; open meteen de meest recente order (of lead)
  // zodat de WO-context direct zichtbaar is.
  useEffect(() => {
    let cancelled = false;
    closeDrawer();
    setLoading(true);
    setError(null);
    setMatch(null);
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) throw new Error('Niet ingelogd');

        const type = customerType;
        const params = new URLSearchParams();
        if (customerId && type === 'account') params.set('accountId', customerId);
        else if (customerId && type === 'lead') params.set('leadId', customerId);
        else if (phone) params.set('phone', phone);
        else throw new Error('Geen klant of nummer om op te zoeken');

        const r = await fetch(`/api/customers/match?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data: CustomerMatch = await r.json();
        if (cancelled) return;
        setMatch(data);
        if (data.found) {
          const firstOrder = data.orders?.[0];
          const firstLead = data.leads?.[0];
          if (firstOrder) openDrawer('order', firstOrder.id);
          else if (firstLead) openDrawer('lead', firstLead.id);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, customerType, phone, openDrawer, closeDrawer]);

  // Reset de gedeelde drawer-store bij sluiten zodat er niets naar de inbox lekt.
  useEffect(() => () => closeDrawer(), [closeDrawer]);

  // Esc sluit de hele slide-over, maar pas als er geen order/offerte open staat
  // (InboxDrawerPanel vangt die eerste Esc zelf af via capture-phase).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || drawerOpenItem) return;
      const t = e.target as HTMLElement;
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT') return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpenItem, onClose]);

  const customer = match?.found ? match.customer : null;
  const fullLink = customer
    ? customer.type === 'lead'
      ? `/offertes/${customer.id}`
      : `/klanten/${customer.id}?type=${customer.type}`
    : null;

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 bg-slate-900/20 z-40"
        aria-hidden="true"
      />
      <aside
        className="fixed top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl z-50 flex flex-col"
        role="complementary"
        aria-label="Klant- en orderdetails"
      >
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-900 truncate">
              {customer?.name || fallbackName || 'Klantcontext'}
            </h2>
            {phone && <div className="text-xs text-slate-500">{phone}</div>}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {fullLink && (
              <Link to={fullLink} className="text-xs text-indigo-600 hover:text-indigo-800 underline">
                Open volledig
              </Link>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Sluit klantcontext"
              className="text-slate-400 hover:text-slate-600 p-1"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden min-h-0">
          {loading ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400">Laden…</div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center text-sm text-red-500 px-6 text-center">
              {error}
            </div>
          ) : !customer ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400 px-6 text-center">
              Geen klant gevonden{phone ? ` voor ${phone}` : ''}.
            </div>
          ) : (
            <>
              <KlantinfoRail customer={match!} activeId={drawerOpenItem?.id} />
              {drawerOpenItem ? (
                <InboxDrawerPanel />
              ) : (
                <div className="flex-1 flex items-center justify-center text-sm text-slate-400 px-6 text-center">
                  Kies een order of offerte in de balk links.
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
